import React from 'react';
import { StepProps } from '../../MortgageApplicationPage';
import {
    MultistepForm,
    SetSubmitError,
    SetIsLoading
} from 'view/components/common';
import { convertCurrencyString } from 'core/utilities';
import { 
    addInvestmentProperty, 
    updateApplicant, 
    updateInvestmentPropertyUnified,
    addMultipleInvestments,
    updateUserAddress,
    upsertDebt,
    getWaitlistPosition
} from 'core/api';
import { useToast } from 'core/toast';
import { formSteps } from './formSteps';
import { useInitialValues, FormValues } from './useInitialValues';
import { ApplicationType } from 'view/pages';

export const Financial : React.FunctionComponent<StepProps> = ({
    step, 
    setStep,
    progressForward,
    progressBackward,
    mortgageType,
    authState,
    application,
    complete,
    refresh
}) => 
{
    const { setToastMessage } = useToast();
    const onExit = () =>
    {
        setStep(s => s - 1);
        progressBackward();
    };

    const {
        applicant,
        investment,
        investmentProperty,
        user_address,
        debtIDs,
        initialValues,
        setInitialValues
    } = useInitialValues(complete);

    const { own } = user_address;

    const onSubmit = (_: SetSubmitError, setIsLoading : SetIsLoading) =>
    {
        return async (payload : FormValues) => 
        {
            const {
                car_debt_amount, 
                car_debt_monthly_payment
            } = payload;

            setInitialValues(v => ({
                ...v,
                car_debt_amount, 
                car_debt_monthly_payment
            }));

            setIsLoading(true);

            const { success, error } = await upsertDebt({
                source : 'a',
                amount : convertCurrencyString(car_debt_amount),
                monthly_payment : convertCurrencyString(car_debt_monthly_payment)
            }, application.id, debtIDs['a']);

            if (success)
            {

                // Application is complete
                if (mortgageType === ApplicationType.PRE_APPROVAL)
                {
                    const { success } = await getWaitlistPosition();
                    if (success)
                    {
                        setTimeout(() =>
                        {
                            setStep(s => s + 2);
                            progressForward();
                        }, 500);
                    }
                    else 
                    {
                        setIsLoading(false);
                        setToastMessage(error);
                    }
                }
                else 
                {
                    setTimeout(() =>
                    {
                        setStep(s => s + 1);
                        progressForward();
                    }, 500);
                }
            }
            else
            {
                setIsLoading(false);
                setToastMessage(error);
            }
        };
    };

    const onNext = {
        1 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const { salary, has_investment_properties } = payload;
            setInitialValues(v => (({
                ...v,
                salary
            })));
            setIsLoading(true);
            
            const { success } = await updateApplicant(authState, { 
                applicationId : application.id,
                applicantId : applicant.id,
                salary : convertCurrencyString(salary)
            });

            if (!success)
            {
                setToastMessage();
                return 0;
            }

            if (!has_investment_properties)
            {
                // Skip investment properties step
                return 2;
            }
        },

        2 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const {
                full_address,
                address,
                suite,
                city,
                region,
                country,
                postal,
                investment_property_value,
                investment_property_balance_owing,
                investment_property_monthly_mortgage_payments,
                investment_property_monthly_property_taxes
            } = payload;

            setInitialValues(v => ({
                ...v,
                full_address,
                address,
                suite,
                city,
                region,
                country,
                postal,
                investment_property_value,
                investment_property_balance_owing,
                investment_property_monthly_mortgage_payments,
                investment_property_monthly_property_taxes
            }));
            setIsLoading(true);

            // Deprecated values required by backend
            const investment_property_monthly_maintenance_fee = 0;
            const investment_property_monthly_rental_income = 0; 

            const debt = {
                amount : convertCurrencyString(investment_property_balance_owing),
                monthly_payment : convertCurrencyString(investment_property_monthly_mortgage_payments)
            };

            const property = {
                _type : '',
                address,
                suite,
                city,
                region,
                country,
                postal,
                monthly_property_tax: convertCurrencyString(investment_property_monthly_property_taxes),
                monthly_maintenance: investment_property_monthly_maintenance_fee,
                gross_monthly_income: investment_property_monthly_rental_income
            };

            if (investmentProperty.id !== undefined)
            {
                const payload = {
                    applicationId: application.id,
                    investment : {
                        id : investmentProperty.id,
                        amount: convertCurrencyString(investment_property_value)
                    },
                    debt : {
                        id : investmentProperty.debtId,
                        ...debt 
                    }, 
                    property : {
                        id : investmentProperty.propertyId,
                        ...property
                    }
                };

                const { error } = await updateInvestmentPropertyUnified(authState, payload);

                if (error)
                {
                    setIsLoading(false);
                    setToastMessage(error);
                }
                else
                {
                    refresh();
                    return 1;
                }

                return 0;
            }
            else 
            {
                const payload = {
                    applicationId: application.id,
                    applicant_id : applicant.id,
                    debt : {
                        source : 'm', // MORTGAGE
                        ...debt 
                    }, 
                    amount: convertCurrencyString(investment_property_value),
                    investment_property : property
                };

                const { error } = await addInvestmentProperty(authState, payload);

                if (error)
                {
                    setIsLoading(false);
                    setToastMessage(error);
                }
                else
                {
                    refresh();
                    return 1;
                }

                return 0;
            }
        },

        3 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const {
                non_reg_cash, 
                rrsp, 
                tfsa, 
                resp
            } = payload;

            const investAccountCodeMap = {
                non_reg_cash : 'c',
                rrsp : 'r',
                tfsa : 't',
                resp : 'e',
            };

            const data = {
                'c' : {
                    id : null,
                    account : {
                        account_type : 'c',
                        amount : convertCurrencyString(non_reg_cash)
                    }
                },
                'r' : {
                    id : null,
                    account : {
                        account_type : 'r',
                        amount : convertCurrencyString(rrsp)
                    }
                },
                't' : {
                    id : null,
                    account : {
                        account_type : 't',
                        amount : convertCurrencyString(tfsa)
                    }
                },
                'e' : {
                    id : null,
                    account : {
                        account_type : 'e',
                        amount : convertCurrencyString(resp)
                    }
                }
            };

            for (const inv of investment)
            {
                if (Object.values(investAccountCodeMap).includes(inv.account_type))
                {
                    const { id, account_type } = inv;
                    // @ts-ignore
                    data[account_type].id = id;
                }
            }

            setInitialValues(v => ({
                ...v,
                non_reg_cash,
                rrsp, 
                tfsa, 
                resp
            }));

            setIsLoading(true);
            const { success } = await addMultipleInvestments(application.id, data);

            if (success)
            {
                if (own) return 1;
                else return 2;
            }
            else 
            {
                refresh();
                setToastMessage();
                return 0;
            }
        },

        4 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const { id } = user_address;
            const { property_value, mortgage_balance, monthly_mortgage, property_tax } = payload;

            setInitialValues(v => ({
                ...v,
                property_value, 
                mortgage_balance, 
                monthly_mortgage, 
                property_tax
            }));

            setIsLoading(true);

            const { success } = await updateUserAddress(id, {
                property_value : convertCurrencyString(property_value), 
                mortgage_balance : convertCurrencyString(mortgage_balance), 
                monthly_mortgage : convertCurrencyString(monthly_mortgage), 
                property_tax : convertCurrencyString(property_tax),
                _type : '',
                monthly_maintenance : 0
            });

            if (success) return 1;
            else
            {
                setToastMessage();
                return 0;
            }
        },

        5 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const { credit_debt_amount, credit_debt_monthly_payment } = payload;
            
            setInitialValues(v => ({
                ...v,
                credit_debt_amount, 
                credit_debt_monthly_payment
            }));

            setIsLoading(true);

            const { success } = await upsertDebt({
                source : 'c',
                amount : convertCurrencyString(credit_debt_amount),
                monthly_payment : convertCurrencyString(credit_debt_monthly_payment)
            }, application.id, debtIDs['c']);

            if (success) return 1;
            else 
            {
                setToastMessage();
                return 0;
            }
        },

        6 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const { credit_line_amount, credit_line_monthly_payment } = payload;

            setInitialValues(v => ({
                ...v,
                credit_line_amount, 
                credit_line_monthly_payment
            }));

            setIsLoading(true);

            const { success } = await upsertDebt({
                source : 'l',
                amount : convertCurrencyString(credit_line_amount),
                monthly_payment : convertCurrencyString(credit_line_monthly_payment)
            }, application.id, debtIDs['l']);

            if (success) return 1;
            else 
            {
                setToastMessage();
                return 0;
            }
        },
        7 : async (payload : FormValues, setIsLoading : SetIsLoading) => 
        {
            const { other_debt_amount, other_debt_monthly_payment } = payload;

            setInitialValues(v => ({
                ...v,
                other_debt_amount, 
                other_debt_monthly_payment
            }));

            setIsLoading(true);

            const { success } = await upsertDebt({
                source : 'o',
                amount : convertCurrencyString(other_debt_amount),
                monthly_payment : convertCurrencyString(other_debt_monthly_payment)
            }, application.id, debtIDs['o']);

            if (success) return 1;
            else 
            {
                setToastMessage();
                return 0;
            }
        }
    };

    const onBack = {
        3 : async (payload : FormValues) =>
        {
            const { has_investment_properties } = payload;
            if (!has_investment_properties)
            {
                // Skip investment properties step
                return 2;
            }
        },
        5 : () =>
        {
            if (own) return 1;
            else return 2;
        }
    };

    return (
        <MultistepForm
            title='Financial Information'
            backText='Back'
            stepTitle={() => `Step ${step}`}
            onExit={onExit}
            initialValues={initialValues}
            formSteps={formSteps}
            onSubmit={onSubmit}
            onNext={onNext}
            onBack={onBack}
        />
    );
};